import { Box, Button, Flashbar, Header, Link, SpaceBetween, Table } from "@cloudscape-design/components";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useDocumentTitle } from "./useDocumentTitle";

/**
 * Lists user-defined dynamic pages and is the authoring entry point: create a new
 * page, edit/delete an existing one, or open its rendered route at /p/:slug.
 * Nav integration is deferred to a later phase, so this page is reachable only by
 * its /pages route (not from the SideNavigation).
 */

interface PageItem {
  documentId: string;
  title: string;
  slug: string;
}

export default function PagesListPage() {
  useDocumentTitle("Pages");
  const navigate = useNavigate();
  const [pages, setPages] = useState<PageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<PageItem[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const apiKey = getAuthToken();
  const selected = selectedItems[0];

  const fetchPages = useCallback(() => {
    if (!apiKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiFetch(`/pages`)
      .then((res) => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then((json) =>
        setPages(
          (json.data ?? []).map((item: any) => ({
            documentId: item.documentId,
            title: item.title,
            slug: item.slug,
          }))
        )
      )
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleDelete = async () => {
    if (!apiKey || !selected) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/pages/${selected.documentId}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setFlash({ type: "success", content: "Page deleted" });
      setDeleteVisible(false);
      setSelectedItems([]);
      fetchPages();
    } catch (e: any) {
      setFlash({ type: "error", content: e.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar
          items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]}
        />
      )}
      <Table
        variant="full-page"
        loading={loading}
        items={pages}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as PageItem[])}
        trackBy="documentId"
        columnDefinitions={[
          { id: "title", header: "Title", cell: (item) => item.title },
          { id: "slug", header: "Slug", cell: (item) => item.slug },
          {
            id: "route",
            header: "Route",
            cell: (item) => (
              <Link href={`/p/${item.slug}`} onFollow={(e) => { e.preventDefault(); navigate(`/p/${item.slug}`); }}>
                /p/{item.slug}
              </Link>
            ),
          },
        ]}
        header={
          <Header
            counter={`(${pages.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={!selected} onClick={() => navigate(`/pages/${selected.documentId}`)}>
                  Edit
                </Button>
                <Button disabled={!selected} onClick={() => setDeleteVisible(true)}>
                  Delete
                </Button>
                <Button variant="primary" onClick={() => navigate(`/pages/new`)}>
                  Create page
                </Button>
              </SpaceBetween>
            }
          >
            Pages
          </Header>
        }
        empty={
          <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
            {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No pages found"}
          </Box>
        }
      />
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete page"
        itemName={selected ? ` ${selected.title}` : undefined}
        loading={deleting}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDelete}
      />
    </SpaceBetween>
  );
}
